import { useEffect, useState } from 'react';
import {
  Trophy, ChevronDown, Percent, ShieldCheck, LogOut,
  ChevronRight, Plus, UserCircle,
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { NetworkScopeSwitcher } from '@/components/navigation/NetworkScopeSwitcher';
import { ProfessorScopeSwitcher } from '@/components/navigation/ProfessorScopeSwitcher';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { useIsPlatformAdmin } from '@/hooks/usePlatformFees';
import { usePlatformCutoverFlag } from '@/hooks/usePlatformCutoverFlag';
import { usePendingMembersCount } from '@/hooks/useClubMembers';
import { usePendingSelfSignupCount } from '@/hooks/useSelfSignupLeads';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { navGroups, professorNavGroups, PLAN_LEVEL, type NavItem, type NavGroup } from '@/lib/navConfig';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem,
  SidebarFooter, SidebarHeader, useSidebar,
} from "@pps/ui/components/ui/sidebar";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@pps/ui/components/ui/collapsible';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@pps/ui/components/ui/tooltip";
import { Logo } from "@pps/ui";

const ppsAdminGroup: NavGroup = {
  labelKey: 'nav.ppsAdmin',
  items: [
    { titleKey: 'nav.platformFees', url: '/admin/platform-fees', icon: Percent },
  ],
};

type NavCollapsibleItemProps = {
  item: NavItem;
  visibleSubItems: NavItem[];
  anySubActive: boolean;
  collapsed: boolean;
  currentLevel: number;
  itemRequiredLevel: number;
  isActive: (path: string) => boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
};

function NavCollapsibleItem({
  item,
  visibleSubItems,
  anySubActive,
  collapsed,
  currentLevel,
  itemRequiredLevel,
  isActive,
  t,
}: NavCollapsibleItemProps) {
  const [open, setOpen] = useState(anySubActive);

  // Sync controlled state when navigating (deep-link or client-side) into a sub-route.
  // Forces open when active sub-route detected; preserves user intent otherwise.
  useEffect(() => {
    if (anySubActive) setOpen(true);
  }, [anySubActive]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={anySubActive}
            className={`rounded-lg transition-colors ${anySubActive ? 'bg-primary/10 text-sidebar-foreground font-medium' : 'text-sidebar-foreground hover:bg-sidebar-accent'}`}
          >
            <item.icon className="h-4 w-4" />
            {!collapsed && <span>{t(item.titleKey)}</span>}
            {!collapsed && (
              <ChevronRight className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            )}
          </SidebarMenuButton>
        </CollapsibleTrigger>
        {!collapsed && (
          <CollapsibleContent>
            <SidebarMenuSub>
              {visibleSubItems.map(sub => {
                const subRequiredLevel = sub.requiredPlan
                  ? PLAN_LEVEL[sub.requiredPlan]
                  : itemRequiredLevel;
                const isSubLocked = currentLevel < subRequiredLevel;
                const subActive = isActive(sub.url);

                if (isSubLocked) {
                  return (
                    <SidebarMenuSubItem key={sub.titleKey}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-muted-foreground/50 cursor-not-allowed select-none text-sm">
                            <sub.icon className="h-4 w-4" />
                            <span>{t(sub.titleKey)}</span>
                            <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 ml-auto border-muted-foreground/30 text-muted-foreground font-normal">
                              Enterprise
                            </Badge>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {t('common.availableOnPlan', { plan: 'Enterprise' })}
                        </TooltipContent>
                      </Tooltip>
                    </SidebarMenuSubItem>
                  );
                }

                return (
                  <SidebarMenuSubItem key={sub.titleKey}>
                    <SidebarMenuSubButton asChild isActive={subActive}>
                      <NavLink
                        to={sub.url}
                        className={`rounded-md transition-colors ${subActive ? 'bg-primary text-primary-foreground font-medium' : 'text-sidebar-foreground hover:bg-sidebar-accent'}`}
                        activeClassName=""
                      >
                        <sub.icon className="h-4 w-4" />
                        <span>{t(sub.titleKey)}</span>
                      </NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, club, clubs, staffRole, selectClub, signOut } = useAuth();
  const { tier, planLabel, planBadgeClass } = usePlanFeatures();
  const { can, isOwner, moduleEnabled, isLoading: permissionsLoading } = usePermissions();
  const { data: isPlatformAdmin = false } = useIsPlatformAdmin();
  const { data: cutoverEnabled = false } = usePlatformCutoverFlag();
  const { t } = useTranslation();

  const { data: pendingMembersCount = 0 } = usePendingMembersCount(club?.id);

  const currentLevel = PLAN_LEVEL[tier] ?? 0;
  const isProfessorMode = club?.kind === 'professor';
  // Staff 'teacher' de clube tradicional: só usado aqui pra esconder o
  // NetworkScopeSwitcher (nao faz sentido pra quem so ve o proprio pool).
  // A nav em si nao usa mais essa flag — vem inteiramente da matriz de
  // permissoes (ver isItemVisible abaixo), que ja reproduz o mesmo recorte.
  const isTeacherStaff = !isProfessorMode && staffRole === 'teacher';
  // Modo professor (P0.4): navegacao reduzida, so quando club.kind==='professor'
  // — nav propria, fora da matriz (ver navConfig.ts e RequirePermission.tsx).
  // Clube tradicional: navGroups completo, filtrado por permissao/modulo.
  const activeNavGroups = isProfessorMode ? professorNavGroups : navGroups;

  // Visibilidade de item de menu: itens sem permissionKey (professorNavGroups)
  // ficam sempre visiveis — modo professor fica fora da matriz. Owner sempre
  // ve tudo. Enquanto o RPC get_my_access_context ainda carrega, mostra tudo
  // (evita flash de menu vazio) — RequirePermission cobre o guard de rota
  // real caso o usuario clique antes do fetch resolver.
  const isItemVisible = (item: NavItem): boolean => {
    if (!item.permissionKey) return true;
    if (isOwner) return true;
    if (permissionsLoading) return true;
    if (item.moduleKey && !moduleEnabled(item.moduleKey)) return false;
    return can(item.permissionKey);
  };

  // Badge de "Cadastros" (P1.2) — so consulta quando o clube ativo e um
  // espaco de professor (query fica `enabled:false` pro resto, custo zero
  // pra clubes tradicionais).
  const { data: pendingSelfSignupCount = 0 } = usePendingSelfSignupCount(
    isProfessorMode ? club?.id : undefined,
  );

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const initials = profile?.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-5">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start'} mb-3`}>
          {collapsed ? (
            <Logo variant="mark" tone="color" size={28} />
          ) : (
            <Logo variant="wordmark" tone="color" size={22} />
          )}
        </div>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex flex-col items-center gap-1.5">
                {club?.logo_url ? (
                  <img src={club.logo_url} alt={club.name} className="h-8 w-8 rounded-lg object-contain" />
                ) : (
                  <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                    <Trophy className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
                <Badge variant="outline" className={`text-[8px] px-1 py-0 h-3.5 border shrink-0 ${planBadgeClass}`}>
                  {planLabel.charAt(0)}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p className="font-semibold">{club?.name}</p>
              <p className="text-xs text-muted-foreground">{t('common.plan')} {planLabel}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="flex items-center gap-3 overflow-hidden">
            {club?.logo_url ? (
              <img src={club.logo_url} alt={club.name} className="h-10 w-10 rounded-lg object-contain shrink-0" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center shrink-0">
                <Trophy className="h-5 w-5 text-primary-foreground" />
              </div>
            )}
            <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
              <div className="flex items-center gap-1.5">
                {clubs.length > 1 ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center gap-1 text-base font-bold text-sidebar-foreground hover:text-primary transition-colors text-left max-w-[140px]">
                      <span className="truncate">{club?.name}</span>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {clubs.map(c => (
                        <DropdownMenuItem key={c.id} onClick={() => selectClub(c)}>{c.name}</DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate('/onboarding/professor')}
                        className="gap-2 text-primary focus:text-primary"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t('professor.onboarding.entryCta')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="text-base font-bold text-sidebar-foreground truncate max-w-[140px]">{club?.name || t('common.myAcademy')}</span>
                    {/* Entry point "Sou professor" (P0.3) para quem tem exatamente 1
                        clube tradicional — nao mostrado se esse unico clube ja for
                        um espaco de professor (evita CTA redundante). Affordance
                        minima (icone 3.5, sem texto) para nao poluir a UI de quem
                        so tem 1 clube tradicional. */}
                    {club?.kind !== 'professor' && (
                      <button
                        type="button"
                        onClick={() => navigate('/onboarding/professor')}
                        className="shrink-0 h-4 w-4 rounded-full flex items-center justify-center text-[hsl(var(--sidebar-muted))] hover:text-primary hover:bg-sidebar-accent transition-colors"
                        title={t('professor.onboarding.entryCta')}
                        aria-label={t('professor.onboarding.entryCta')}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className={`text-[9px] px-1 py-0 h-4 border shrink-0 ${planBadgeClass}`}>
                  {planLabel}
                </Badge>
                <span className="text-xs text-muted-foreground truncate">{club?.city}{club?.state ? `, ${club.state}` : ''}</span>
              </div>
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="px-2 py-2">
        <TooltipProvider delayDuration={300}>
          {isProfessorMode ? <ProfessorScopeSwitcher /> : isTeacherStaff ? null : <NetworkScopeSwitcher />}
          {activeNavGroups.map(group => {
            const groupRequiredLevel = group.requiredPlan ? PLAN_LEVEL[group.requiredPlan] : 0;
            const isGroupLocked = currentLevel < groupRequiredLevel;

            // Filter items by permission (matriz de RBAC granular)
            const visibleItems = group.items.filter(isItemVisible);

            if (visibleItems.length === 0) return null;

            return (
              <SidebarGroup key={group.labelKey}>
                <SidebarGroupLabel className="text-[10px] font-semibold tracking-widest uppercase text-[hsl(var(--sidebar-muted))] px-3 mb-1">
                  {t(group.labelKey)}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleItems.map(item => {
                      const active = isActive(item.url);
                      const itemRequiredLevel = item.requiredPlan ? PLAN_LEVEL[item.requiredPlan] : groupRequiredLevel;
                      const isItemLocked = currentLevel < itemRequiredLevel;

                      if (isItemLocked) {
                        return (
                          <SidebarMenuItem key={item.titleKey}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground/50 cursor-not-allowed select-none">
                                  <item.icon className="h-4 w-4" />
                                  {!collapsed && <span className="text-sm">{t(item.titleKey)}</span>}
                                  {!collapsed && (
                                    <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 ml-auto border-muted-foreground/30 text-muted-foreground font-normal">
                                      Enterprise
                                    </Badge>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                {t('common.availableOnPlan', { plan: 'Enterprise' })}
                              </TooltipContent>
                            </Tooltip>
                          </SidebarMenuItem>
                        );
                      }

                      const showMembersBadge = item.url === '/membros' && pendingMembersCount > 0;
                      const showSignupsBadge = item.url === '/professor/cadastros' && pendingSelfSignupCount > 0;
                      const showBadge = showMembersBadge || showSignupsBadge;
                      const badgeCount = showMembersBadge ? pendingMembersCount : pendingSelfSignupCount;

                      if (item.subItems && item.subItems.length > 0) {
                        const visibleSubItems = item.subItems.filter(isItemVisible);
                        if (visibleSubItems.length === 0) return null;
                        const anySubActive = visibleSubItems.some(sub => isActive(sub.url));
                        return (
                          <NavCollapsibleItem
                            key={item.titleKey}
                            item={item}
                            visibleSubItems={visibleSubItems}
                            anySubActive={anySubActive}
                            collapsed={collapsed}
                            currentLevel={currentLevel}
                            itemRequiredLevel={itemRequiredLevel}
                            isActive={isActive}
                            t={t}
                          />
                        );
                      }

                      return (
                        <SidebarMenuItem key={item.titleKey}>
                          <SidebarMenuButton asChild isActive={active}>
                            <NavLink
                              to={item.url}
                              end={item.url === '/'}
                              className={`rounded-lg transition-colors ${active ? 'bg-primary text-primary-foreground font-medium' : 'text-sidebar-foreground hover:bg-sidebar-accent'}`}
                              activeClassName=""
                            >
                              <item.icon className="h-4 w-4" />
                              {!collapsed && <span>{t(item.titleKey)}</span>}
                              {!collapsed && showBadge && (
                                <Badge className="ml-auto h-5 min-w-5 px-1 text-[10px] rounded-md bg-destructive text-destructive-foreground border-0 flex items-center justify-center">
                                  {badgeCount}
                                </Badge>
                              )}
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}

          {isPlatformAdmin && !cutoverEnabled && (
            <SidebarGroup key={ppsAdminGroup.labelKey}>
              <SidebarGroupLabel className="text-[10px] font-semibold tracking-widest uppercase text-[hsl(var(--sidebar-muted))] px-3 mb-1 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" />
                {t(ppsAdminGroup.labelKey)}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {ppsAdminGroup.items.map(item => {
                    const active = isActive(item.url);
                    return (
                      <SidebarMenuItem key={item.titleKey}>
                        <SidebarMenuButton asChild isActive={active}>
                          <NavLink
                            to={item.url}
                            className={`rounded-lg transition-colors ${active ? 'bg-primary text-primary-foreground font-medium' : 'text-sidebar-foreground hover:bg-sidebar-accent'}`}
                            activeClassName=""
                          >
                            <item.icon className="h-4 w-4" />
                            {!collapsed && <span>{t(item.titleKey)}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </TooltipProvider>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3">
        {/* Clicar no nome abre o menu de conta (antes era um <p> inerte —
            pedido: mesmas opções do site público). Collapsed: só o Avatar. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex w-full items-center gap-3 rounded-md p-1 text-left transition-colors hover:bg-sidebar-accent"
              title={t('account.menu')}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-sidebar-foreground truncate">{profile?.full_name}</p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-52">
            <DropdownMenuItem onClick={() => navigate('/minha-conta')}>
              <UserCircle className="mr-2 h-4 w-4" /> {t('account.menu')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut}>
              <LogOut className="mr-2 h-4 w-4" /> {t('common.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
